import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { adminService } from '../../services/admin';
import {
  IndividualMemberDto,
  MembershipType,
  WorkSector,
  getMemberDisplayFirstName,
  getMemberDisplayLastName,
} from '../../types/member';
import { PagedResponse } from '../../types/member';
import { Container, Stack } from '../../design-system/components/Layout';
import { Card } from '../../design-system/components/Card';
import { Button } from '../../design-system/components/Button';
import { Heading, Label as DSLabel } from '../../design-system/components/Typography';
import { PageHeader } from '../../design-system/patterns/PageHeader';
import SearchBar from '../../components/common/SearchBar';
import { DataTable, Column } from '../../components/common/DataTable';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { animations } from '../../design-system/tokens/animations';
import { shadows } from '../../design-system/tokens/shadows';
import { enumMatches, tryNormalizeEnumValue } from '../../utils/enumNormalize';
import { formatMemberCardNumberForDisplay } from '../../utils/memberCardNumber';

const AdminMembersContainer = styled(Container)`
  max-width: 1400px;
`;
const FiltersSection = styled(Card)`
  padding: ${spacing[6]};
  margin-bottom: ${spacing[6]};
`;
const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing[4]};
  align-items: end;
`;
const FilterLabel = styled.label`
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
`;
const SelectStyled = styled.select`
  padding: ${spacing[3]} ${spacing[4]};
  border: ${borders.width.base} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.xl};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  transition: ${animations.transition(
    ['border-color', 'box-shadow'],
    animations.duration.normal,
    animations.easing.easeInOut
  )};
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => shadows.colored.sm(props.theme.colors.primary)};
  }
`;
const NameCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
`;
const Initials = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${borders.radius.full};
  background: ${props => props.theme.colors.gradientSecondary};
  color: ${props => props.theme.colors.text};
  font-weight: ${typography.fontWeight.semibold};
  font-size: ${typography.fontSize.xs};
  font-family: ${typography.fontFamily.display};
  flex-shrink: 0;
`;
const PAGE_SIZE = 25;

function getResidentialCityDisplay(member: IndividualMemberDto): string {
  const en = member.residential?.cityEn?.trim() || '';
  const gr = member.residential?.cityGr?.trim() || '';
  if (en && gr && en !== gr) return `${gr} / ${en}`;
  return gr || en || member.professionalCity?.trim() || '—';
}

const AdminMembersList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [members, setMembers] = useState<IndividualMemberDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [membershipTypeFilter, setMembershipTypeFilter] = useState<string>('');
  const [workSectorFilter, setWorkSectorFilter] = useState<string>('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const loadMembers = async () => {
      setLoading(true);
      try {
        const response: PagedResponse<IndividualMemberDto> = await adminService.getMembers({
          page,
          pageSize: PAGE_SIZE,
          search: debouncedSearch || undefined,
          membershipType: membershipTypeFilter ? parseInt(membershipTypeFilter, 10) : undefined,
        });
        setMembers(response.items);
        setTotal(response.total);
      } catch (error) {
        console.error('Error loading members:', error);
      } finally {
        setLoading(false);
      }
    };
    loadMembers();
  }, [page, debouncedSearch, membershipTypeFilter]);

  const handleMemberClick = (member: IndividualMemberDto) => {
    navigate(`/admin/members/${member.id}`);
  };

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setMembershipTypeFilter('');
    setWorkSectorFilter('');
    setPage(1);
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    const first = (firstName ?? '').trim();
    const last = (lastName ?? '').trim();
    if (!first && !last) return '—';
    if (!first) return last.substring(0, 2).toUpperCase();
    if (!last) return first.substring(0, 2).toUpperCase();
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  };

  const getMembershipTypeLabel = (type?: MembershipType | string | number | null): string => {
    const normalized = tryNormalizeEnumValue(MembershipType, type);
    if (normalized == null) return '—';
    const labels: Record<MembershipType, string> = {
      [MembershipType.Regular]: t('membership.regular'),
      [MembershipType.Student]: t('membership.student'),
      [MembershipType.Retired]: t('membership.retired'),
      [MembershipType.Honorary]: t('membership.honorary'),
    };
    return labels[normalized] || '—';
  };

  const getWorkSectorLabel = (sector?: WorkSector | string | number | null): string => {
    const normalized = tryNormalizeEnumValue(WorkSector, sector);
    if (normalized == null) return '—';
    const labels: Record<WorkSector, string> = {
      [WorkSector.Museology]: t('workSector.museology'),
      [WorkSector.Education]: t('workSector.education'),
      [WorkSector.Documentation]: t('workSector.documentation'),
      [WorkSector.Conservation]: t('workSector.conservation'),
      [WorkSector.Exhibition]: t('workSector.exhibition'),
      [WorkSector.Management]: t('workSector.management'),
      [WorkSector.Communication]: t('workSector.communication'),
      [WorkSector.Other]: t('workSector.other'),
    };
    return labels[normalized] || '—';
  };

  const filteredMembers = members.filter((member) => {
    if (!workSectorFilter) return true;
    const expected = parseInt(workSectorFilter, 10) as WorkSector;
    return enumMatches(WorkSector, member.workSector, expected);
  });

  const hasActiveFilters = !!search || !!membershipTypeFilter || !!workSectorFilter;

  const memberColumns: Column<IndividualMemberDto>[] = [
    {
      key: 'name',
      header: t('admin.name'),
      render: (member) => (
        <NameCell>
          <Initials>{getInitials(getMemberDisplayFirstName(member), getMemberDisplayLastName(member))}</Initials>
          <span>{getMemberDisplayFirstName(member)} {getMemberDisplayLastName(member)}</span>
        </NameCell>
      ),
    },
    {
      key: 'email',
      header: t('admin.email'),
      render: (member) => member.email || '—',
    },
    {
      key: 'memberCardNumber',
      header: t('register.memberCardNumber'),
      render: (member) => formatMemberCardNumberForDisplay(member.memberCardNumber) || '—',
    },
    {
      key: 'membershipType',
      header: t('admin.membershipType'),
      render: (member) => getMembershipTypeLabel(member.membershipType),
    },
    {
      key: 'workSector',
      header: t('admin.workSector'),
      render: (member) => getWorkSectorLabel(member.workSector),
    },
    {
      key: 'city',
      header: t('admin.city'),
      render: (member) => getResidentialCityDisplay(member),
    },
    {
      key: 'registrationYear',
      header: t('admin.registrationYear'),
      render: (member) => member.registrationYear ?? '—',
    },
    {
      key: 'actions',
      header: t('admin.actions'),
      actions: (member) => (
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleMemberClick(member);
          }}
        >
          {t('admin.view')}
        </Button>
      ),
    },
  ];

  return (
    <AdminMembersContainer>
      <Stack direction="column" spacing="xl">
        <PageHeader
          title={t('admin.members')}
          subtitle={!loading ? (t('admin.showingResults', {
            start: (page - 1) * PAGE_SIZE + 1,
            end: Math.min(page * PAGE_SIZE, total),
            total,
          }) || `Showing ${(page - 1) * PAGE_SIZE + 1}-${Math.min(page * PAGE_SIZE, total)} of ${total} members`) : undefined}
          pattern={true}
          actions={
            <Button variant="primary" onClick={() => navigate('/register-individual?mode=admin')}>
              {t('admin.addIndividualMember')}
            </Button>
          }
        />

        <FiltersSection variant="elevated" accentBorder="top">
          <Stack direction="column" spacing="md">
            <Heading variant="h3" color="text">
              {t('admin.filters')}
            </Heading>
            <FiltersGrid>
              <Stack direction="column" spacing="sm">
                <FilterLabel>{t('common.search')}</FilterLabel>
                <SearchBar
                  value={search}
                  onChange={setSearch}
                  placeholder={t('admin.searchPlaceholder')}
                  fullWidth
                />
              </Stack>
              <Stack direction="column" spacing="sm">
                <FilterLabel htmlFor="admin-members-membership-type">{t('admin.membershipType')}</FilterLabel>
                <SelectStyled
                  id="admin-members-membership-type"
                  value={membershipTypeFilter}
                  onChange={(e) => {
                    setMembershipTypeFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t('admin.all')}</option>
                  <option value={MembershipType.Regular.toString()}>{t('membership.regular')}</option>
                  <option value={MembershipType.Student.toString()}>{t('membership.student')}</option>
                  <option value={MembershipType.Retired.toString()}>{t('membership.retired')}</option>
                  <option value={MembershipType.Honorary.toString()}>{t('membership.honorary')}</option>
                </SelectStyled>
              </Stack>
              <Stack direction="column" spacing="sm">
                <FilterLabel htmlFor="admin-members-work-sector">{t('admin.workSector')}</FilterLabel>
                <SelectStyled
                  id="admin-members-work-sector"
                  value={workSectorFilter}
                  onChange={(e) => {
                    setWorkSectorFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t('admin.all')}</option>
                  <option value={WorkSector.Museology.toString()}>{t('workSector.museology')}</option>
                  <option value={WorkSector.Education.toString()}>{t('workSector.education')}</option>
                  <option value={WorkSector.Documentation.toString()}>{t('workSector.documentation')}</option>
                  <option value={WorkSector.Conservation.toString()}>{t('workSector.conservation')}</option>
                  <option value={WorkSector.Exhibition.toString()}>{t('workSector.exhibition')}</option>
                  <option value={WorkSector.Other.toString()}>{t('workSector.other')}</option>
                </SelectStyled>
              </Stack>
              {hasActiveFilters && (
                <Stack direction="row" justify="end">
                  <Button variant="outline" onClick={clearFilters}>
                    {t('admin.clearFilters')}
                  </Button>
                </Stack>
              )}
            </FiltersGrid>
          </Stack>
        </FiltersSection>

        <DataTable<IndividualMemberDto>
          data={filteredMembers}
          columns={memberColumns}
          loading={loading}
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
          onRowClick={handleMemberClick}
          emptyMessage={t('admin.noMembers')}
        />
      </Stack>
    </AdminMembersContainer>
  );
};
export default AdminMembersList;
